import { InfiniteData, QueryFunctionContext, useInfiniteQuery } from 'react-query';
import { httpClient } from 'src/utils/HttpClients';
import type { TUser, TUsersResponse } from 'src/types';
import unionWith from 'lodash/unionWith';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import { useAppState } from 'src/context';

export const useGetUsers = (onlyMyTeam?: boolean) => {
    const {
        values: { users },
        actions: { handleSetUsers },
    } = useAppState();
    const queryFn = async (context: QueryFunctionContext<string[]> | undefined) => {
        const pageParam = context?.pageParam ?? 1;
        const params = {
            page: pageParam,
            itemsPerPage: 200,
            format: 'manager',
            action: onlyMyTeam ? 'team' : null,
        };

        const response = await httpClient.get<{ data: TUser[] }>('/users/list', {
            params: omitBy(params, isNil),
        });

        return {
            data: get(response.data, 'data', []),
            metadata: get(response.data, 'metadata', {
                currentPage: 1,
                totalPages: 1,
            }),
        };
    };

    const onSettled = async (data: InfiniteData<TUsersResponse[]> | undefined) => {
        const pages = get(data, 'pages', []) as Array<TUsersResponse>;
        const usersList = pages.reduce(
            (acc: TUser[], page: { data: TUser[] }) => [...acc, ...page?.data],
            []
        );
        const grouped = unionWith(users, usersList, isEqual);
        handleSetUsers(grouped);
    };

    return useInfiniteQuery(['users'], queryFn, {
        getNextPageParam: ({ metadata }) =>
            metadata.currentPage < metadata.totalPages ? metadata.currentPage + 1 : undefined,
        onSettled,
        refetchOnMount: false,
    });
};
